import {
  cloneDeep,
  every,
  filter,
  identity,
  isEqual,
  keyBy,
  map,
  mapValues,
  values,
  uniq,
  uniqBy
} from 'lodash';

import {
  PREFILTER_ACCOUNTS,
  SELECTED_ACCOUNT_FILTER,
  TOGGLED_PURPOSE,
  TOGGLED_ALL_PURPOSES,
  TOGGLED_PRODUCT,
  TOGGLED_ALL_PRODUCTS,
  MOBILE_TOGGLED_CHANGE_FILTERS
} from 'state/Actions';

import { RECEIVED_ACCOUNTS } from 'state/account/AccountBalancesActions';

function accountInFilters(account, selections, fullState) {
  if (account == undefined) {
    return false;
  }

  const { accountsPreFilter, accountFilter } = selections;

  if (
    accountsPreFilter &&
    !accountsPreFilter.all &&
    !accountsPreFilter.subset[account.accountId]
  ) {
    return false;
  }

  if (accountFilter === 'product') {
    if (selections.products.all) {
      return true;
    }
    let productKey = fullState.products.byAccount[account.productType].key;
    return selections.products.subset[productKey];
  }

  if (accountFilter === 'purpose') {
    if (selections.purposes.all) {
      return true;
    }
    let purposeType = account.retirementAccount
      ? 'retirement'
      : 'nonRetirement';
    return selections.purposes.subset[purposeType];
  }
}

export const selectionsReducer = (state = {}, action, fullState) => {
  const selections = cloneDeep(state);
  const { purposes, products } = selections;
  const { productTypes, accounts } = fullState;
  const purposeTypes = fullState.purposes;
  const availableProducts = fullState.products.types;

  if (action.type === RECEIVED_ACCOUNTS) {
    selections.accountsPreFilter.subset = accounts.reduce((obj, account) => {
      obj[account.accountId] = true;
      return obj;
    }, {});

    const userProducts = keyBy(
      filter(availableProducts, (a) => a.userAccountNumbers.length > 0),
      (x) => x.key
    );
    selections.accountsPreFilter.all = values(
      selections.accountsPreFilter.subset
    ).every((a) => a === true);
    selections.productsPreFilter.subset = mapValues(userProducts, () => true);
    selections.purposesPreFilter.subset = mapValues(purposeTypes, () => true);
  }

  if (action.type === PREFILTER_ACCOUNTS) {
    selections.purposes.all = true;
    selections.purposes.subset = mapValues(purposeTypes, () => false);
    selections.products.all = true;
    selections.products.subset = mapValues(productTypes, () => false);

    selections.accountsPreFilter = {
      all: action.all,
      subset: action.subset,
      selectedCount: action.all
        ? accounts.length
        : Object.keys(action.subset).filter((a) => action.subset[a]).length,
      totalCount: accounts.length
    };

    const preFilterAccountList = accounts.filter(
      (a) => action.subset[a.accountId]
    );
    const preFilterProductList =
      accountArrayToProductTypeKeyArray(preFilterAccountList);
    const preFilterPurposeBoolList = uniq(
      preFilterAccountList.map((a) => a.retirementAccount)
    );

    const preFilterProductAll =
      preFilterProductList.length === Object.keys(products.subset).length;
    const preFilterPurposeAll = preFilterPurposeBoolList.length === 2;

    selections.productsPreFilter = {
      all: preFilterProductAll,
      subset: preFilterProductList.reduce(
        (subset, product) => ((subset[product] = true), subset),
        {}
      )
    };
    selections.purposesPreFilter = {
      all: preFilterPurposeAll,
      subset: preFilterPurposeBoolList.reduce(
        (subset, isRetirement) => (
          (subset[isRetirement ? 'retirement' : 'nonRetirement'] = true), subset
        ),
        {}
      )
    };
  } else if (action.type === SELECTED_ACCOUNT_FILTER) {
    selections.accountFilter = action.accountFilter;
  } else if (action.type === TOGGLED_PURPOSE) {
    purposes.subset[action.purpose] = !purposes.subset[action.purpose];
    purposes.all = Object.keys(purposes.subset).every(
      (k) => !purposes.subset[k]
    );
  } else if (action.type === TOGGLED_ALL_PURPOSES) {
    purposes.all = true;
    purposes.subset = mapValues(purposes.subset, () => false);
  } else if (action.type === TOGGLED_PRODUCT) {
    products.subset[action.product] = !products.subset[action.product];
    products.all = Object.keys(products.subset).every(
      (k) => !products.subset[k]
    );
  } else if (action.type === TOGGLED_ALL_PRODUCTS) {
    products.all = true;
    products.subset = mapValues(products.subset, () => false);
  } else if (action.type === MOBILE_TOGGLED_CHANGE_FILTERS) {
    /* User prompts filter change on data.
     Copy all temporary mobileFilterSelections to the selections state object */
    if (fullState && fullState.mobileFilterSelections) {
      selections.accountFilter = fullState.mobileFilterSelections.accountFilter;
      selections.products = cloneDeep(
        fullState.mobileFilterSelections.products
      );
      selections.purposes = cloneDeep(
        fullState.mobileFilterSelections.purposes
      );
      selections.period = fullState.mobileFilterSelections.period;
    }
  }

  if (fullState && accounts && accounts.length) {
    selections.accounts = accounts.filter((a) =>
      accountInFilters(a, selections, fullState)
    );
    selections.accountNumbers = keyBy(
      selections.accounts,
      (a) => a.accountNumber
    );
    selections.accountIds = keyBy(selections.accounts, (a) => a.accountId);
    selections.productClasses = accountArrayToProductTypeKeyArray(
      selections.accounts
    );

    if (selections.accountsPreFilter.totalCount === 0) {
      selections.accountsPreFilter.totalCount =
        selections.accountsPreFilter.selectedCount = accounts.length;
    }
  } else {
    selections.accounts = [];
    selections.accountNumbers = {};
    selections.accountIds = {};
    selections.productClasses = [];
  }

  selections.brokerageOnly = selections.accounts.length
    ? every(
        selections.accounts,
        (a) => a.productType === fullState.products.providers.brokerage
      )
    : false;
  selections.activePlusOnly = selections.accounts.length
    ? every(
        selections.accounts,
        (a) => a.productType === fullState.products.providers.activePlus
      )
    : false;

  return isEqual(selections, state) ? state : selections;

  function accountArrayToProductTypeKeyArray(accounts) {
    return uniqBy(
      map(accounts, (a) => {
        return (fullState.products.byAccount[a.productType] || {}).key;
      }),
      identity
    );
  }
};

selectionsReducer.priority = 16;
